<!-- src\views\components\MediaHistoryTable.vue -->
<template>
  <div class="card mb-4">
    <!-- UPDATED: Improved flex classes for responsive stacking -->
    <div class="card-header pb-0 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center flex-wrap gap-3">
      <h6 class="mb-0">{{ $t('media') }}</h6>
      
      <div class="d-flex flex-column flex-sm-row align-items-start align-items-sm-center flex-wrap gap-3 w-100 w-md-auto">
        <!-- VIEW TOGGLE BUTTONS -->
        <div class="btn-group btn-group-sm shadow-sm">
          <button 
            class="btn btn-sm mb-0" 
            :class="viewMode === 'table' ? 'btn-primary text-white' : 'btn-outline-primary'" 
            @click="viewMode = 'table'"
            :style="viewMode === 'table' ? { backgroundColor: 'var(--bs-primary)', borderColor: 'var(--bs-primary)' } : { color: 'var(--bs-primary)', borderColor: 'var(--bs-primary)' }"
          >
            <i class="fas fa-table"></i>
          </button>
          <button 
            class="btn btn-sm mb-0" 
            :class="viewMode === 'card' ? 'btn-primary text-white' : 'btn-outline-primary'" 
            @click="viewMode = 'card'"
            :style="viewMode === 'card' ? { backgroundColor: 'var(--bs-primary)', borderColor: 'var(--bs-primary)' } : { color: 'var(--bs-primary)', borderColor: 'var(--bs-primary)' }"
          >
            <i class="fas fa-th-large"></i>
          </button>
        </div>

        <!-- Filter Section -->
        <!-- UPDATED: Added flex-wrap and responsive width for mobile filter inputs -->
        <div class="d-flex flex-wrap align-items-center gap-2 w-100 w-sm-auto">
          <input 
            type="date" 
            v-model="startDate" 
            class="filter-input flex-grow-1 flex-sm-grow-0" 
            placeholder="Start Date" 
          />
          <input 
            type="date" 
            v-model="endDate" 
            class="filter-input flex-grow-1 flex-sm-grow-0" 
            placeholder="End Date" 
          />
          
          <div class="d-flex gap-2 w-100 w-sm-auto">
            <button 
              @click="applyFilters" 
              class="btn btn-primary filter-btn mb-0 flex-grow-1"
              :style="{ backgroundColor: 'var(--bs-primary)', borderColor: 'var(--bs-primary)' }"
            >
              {{ $t('search') }}
            </button>
            <button 
              v-if="startDate && endDate" 
              @click="clearFilters" 
              class="btn btn-secondary filter-btn mb-0 flex-grow-1"
            >
              {{ $t('clear') }}
            </button>
            <!-- Export to Excel Button -->
            <button 
              @click="exportToExcel" 
              class="btn btn-success filter-btn mb-0 flex-grow-1"
              :style="{ backgroundColor: 'var(--bs-primary)', borderColor: 'var(--bs-primary)' }"
            >
              <i class="fas fa-file-excel d-sm-none"></i>
              <span class="d-none d-sm-inline">{{ $t('export') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="card-body px-0 pt-0 pb-2">
      <!-- 1. TABLE VIEW -->
      <div v-if="viewMode === 'table'" class="table-responsive p-0">
        <table class="table align-items-center mb-0">
          <thead>
            <tr>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-left">
                User Name
              </th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-left">
                Device ID
              </th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-left">
                {{ $t('media_name') }}
              </th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-left">
                Media Count
              </th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-left">
                Display Time
              </th>
              <!-- NEW HEADERS FOR EXPIRY LOGIC -->
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-left">
                Expiry Date
              </th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-left">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(media, index) in paginatedRoles" :key="index">
              <td class="text-left">
                <p class="text-xs font-weight-bold mb-0">{{ media.username }}</p>
              </td>
              <td class="text-left">
                <p class="text-xs font-weight-bold mb-0">{{ media.device_id }}</p>
              </td>
              <td class="text-left">
                <p class="text-xs font-weight-bold mb-0">{{ media.media_name }}</p>
              </td>
              <td class="text-left">
                <p class="text-xs font-weight-bold mb-0">{{ media.count }}</p>
              </td>
              <td class="text-left">
                <p class="text-xs font-weight-bold mb-0">{{ media.time_sum }}</p>
              </td>
              <!-- NEW CELL: EXPIRY DATE -->
              <td class="text-left">
                <p class="text-xs font-weight-bold mb-0">{{ media.expiry_date || 'N/A' }}</p>
              </td>
              <!-- NEW CELL: STATUS BADGE -->
              <td class="text-left">
                <span :class="getStatusClass(media.expiry_date)" class="badge badge-sm">
                  {{ getStatusText(media.expiry_date) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 2. CARD VIEW -->
      <div v-else class="px-3 px-md-4 pb-4 pt-3">
        <div class="row">
          <!-- UPDATED: col-12 for mobile -->
          <div v-for="(media, index) in paginatedRoles" :key="'card-' + index" class="col-12 col-md-6 col-xl-3 mb-4">
            <div class="card h-100 border shadow-sm">
              <div class="card-body p-3">
                <div class="d-flex justify-content-between align-items-start mb-3">
                  <div class="overflow-hidden">
                    <h6 class="mb-0 text-sm text-truncate" :title="media.media_name">{{ media.media_name }}</h6>
                    <p class="text-xxs text-secondary mb-0">Device: {{ media.device_id }}</p>
                  </div>
                  <span :class="getStatusClass(media.expiry_date)" class="badge badge-sm">
                    {{ getStatusText(media.expiry_date) }}
                  </span>
                </div>
                
                <div class="mb-3">
                  <p class="text-xs mb-1"><strong><i class="fas fa-user me-1"></i> User:</strong> {{ media.username }}</p>
                  <p class="text-xs mb-1"><strong><i class="fas fa-chart-line me-1"></i> Count:</strong> {{ media.count }}</p>
                  <p class="text-xs mb-1"><strong><i class="fas fa-clock me-1"></i> Total Time:</strong> {{ media.time_sum }}</p>
                </div>

                <div class="pt-2 border-top">
                  <p class="text-xxs text-secondary mb-0"><strong>Expiry:</strong> {{ media.expiry_date || 'N/A' }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No Results Case -->
      <div v-if="roles.length === 0" class="text-center py-5">
        <p class="text-secondary text-sm">No media history records found.</p>
      </div>

      <!-- UPDATED: Added flex-wrap for mobile pagination -->
      <div class="pagination flex-wrap">
        <button @click="prevPage" :disabled="currentPage === 1" :style="{ backgroundColor: 'var(--bs-primary)', borderColor: 'var(--bs-primary)' }">{{ $t('previous') }}</button>
        <button v-for="page in totalPages" :key="page" @click="goToPage(page)" :class="{ active: currentPage === page }" :style="currentPage === page ? { backgroundColor: 'var(--bs-primary)', borderColor: 'var(--bs-primary)' } : {}">
          {{ page }}
        </button>
        <button @click="nextPage" :disabled="currentPage === totalPages" :style="{ backgroundColor: 'var(--bs-primary)', borderColor: 'var(--bs-primary)' }">{{ $t('next') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import '@fortawesome/fontawesome-free/css/all.css';
import MediaHistoryService from "../../services/MediaHistoryService.js";
import * as XLSX from 'xlsx';

export default {
  name: "MediaHistoryTable",
  data() {
    return {
      viewMode: 'card', // Updated default to 'card' as requested
      isLoading: false,
      roles: [], // Stores the media items
      currentPage: 1,
      itemsPerPage: 10,
      selectedLanguage: this.$i18n.locale,
      errors: {},
      startDate: '',
      endDate: '',
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.roles.length / this.itemsPerPage);
    },
    paginatedRoles() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.roles.slice(start, end);
    },
  },
  created() {
    this.getAllMediaHistory();
  },
  methods: {
    switchLanguage(lang) {
      this.$i18n.locale = lang;
    },
    getAllMediaHistory() {
      MediaHistoryService.getAll()
        .then(response => {
          this.roles = response.data.flatMap(item => item.media);
          console.log(this.roles);
        })
        .catch(e => {
          console.log(e);
        });
    },
    getAllMediaFilterHistory(startDate, endDate) {
      const params = {
        start_date: startDate,
        end_date: endDate,
      };

      this.isLoading = true;

      MediaHistoryService.getAllMediaFilterHistory(params.start_date, params.end_date)
        .then(response => {
          this.roles = response.data.flatMap(item => item.media);
          this.currentPage = 1;
          console.log(this.roles);
        })
        .catch(e => {
          console.log(e);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    applyFilters() {
      console.log('Applying filters:', this.startDate, this.endDate);
      this.getAllMediaFilterHistory(this.startDate, this.endDate);
    },
    clearFilters() {
      this.startDate = '';
      this.endDate = '';
      this.getAllMediaHistory();
    },
    // Logic to determine Status Text
    getStatusText(expiryDate) {
      if (!expiryDate) return "Active";
      const today = new Date().toISOString().split('T')[0];
      return expiryDate < today ? "Expired" : "Active";
    },
    // Logic to determine Badge Class
    getStatusClass(expiryDate) {
      if (!expiryDate) return "bg-gradient-success";
      const today = new Date().toISOString().split('T')[0];
      return expiryDate < today ? "bg-gradient-danger" : "bg-gradient-success";
    },
    exportToExcel() {
      // Create a new array of the data including "Media Preview" and Expiry Info
      const exportData = this.roles.map(item => ({
        'User Name': item.username,
        'Device ID': item.device_id,
        'Media Name': item.media_name,
        'Media Count': item.count,
        'Display Time': item.time_sum,
        'Expiry Date': item.expiry_date || 'N/A',
        'Status': this.getStatusText(item.expiry_date)
      }));

      // Create a worksheet from the export data
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Create a workbook with the worksheet
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Media History');

      // Export the workbook to Excel
      XLSX.writeFile(wb, 'Media_History.xlsx');
    }
  },
};
</script>


<style scoped>
/* Updated: Status Badge Styles use dynamic theme variables */
.bg-gradient-success {
  background-image: linear-gradient(310deg, var(--bs-primary) 0%, var(--bs-primary) 100%) !important;
  background-color: var(--bs-primary) !important;
  color: #fff;
}

.bg-gradient-danger {
  background-image: linear-gradient(310deg, #ea0606 0%, #ff667c 100%);
  color: #fff;
}

.badge {
  text-transform: uppercase;
  line-height: 0.75;
  padding: 0.55em 0.9em;
}

.langdrop {
  width: 130px;
  margin-left: 87%;
}

@media (min-width:300px) and (max-width:550px) {
  .langdrop {
    width: 130px;
    margin-left: 57%;
  }
}

.table th,
.table td {
  text-align: left;
  padding: 12px;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 1rem;
}

.pagination button {
  background-color: var(--bs-primary);
  color: white;
  border: 2px solid var(--bs-primary);
  padding: 0.5rem 1rem;
  border-radius: 10px;
  margin: 0.5rem; /* Updated for wrapping */
  cursor: pointer;
}

.pagination button:disabled {
  background-color: #ffffff;
  color: var(--bs-primary);
  cursor: not-allowed;
  opacity: 0.6;
}

.pagination button.active {
  background-color: white !important;
  color: var(--bs-primary) !important;
  border-color: var(--bs-primary) !important;
  border-radius: 50px;
  font-weight: bold;
}

/* Improved filter section styles */
.filter-input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  width: 150px;
  transition: all 0.3s ease;
}

.filter-input:focus {
  border-color: var(--bs-primary) !important;
  outline: none;
  box-shadow: 0 0 0 2px rgba(0,0,0,0.05);
}

.filter-btn {
  padding: 8px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.filter-btn:hover {
  filter: brightness(1.1);
}

.filter-btn:disabled {
  background-color: #c4c4c4;
  cursor: not-allowed;
}

.btn{
  margin-bottom: 0px;
}

/* FORCE OVERRIDE FOR NEW THEME */

.btn-primary, .pagination button:not(.active) {
    background-color: var(--bs-primary) !important;
    border-color: var(--bs-primary) !important;
    color: white !important;
}

.btn-success, .bg-gradient-success {
    background-image: none !important;
    background-color: var(--bs-primary) !important;
    border-color: var(--bs-primary) !important;
    color: white !important;
}

/* --- ADDED MOBILE RESPONSIVE CSS --- */
@media (max-width: 768px) {
  .filter-input {
    width: 100%;
  }
  .card-header {
    padding-bottom: 20px !important;
  }
  .pagination button {
    padding: 0.4rem 0.8rem;
    font-size: 12px;
  }
}
</style>